import type React from 'react';

import type { EventId } from '@vlabs/api/luna-api/model';
import type { WithDataTestid, WithClassNameProp } from '@vlabs/uikit/types';
import cn from 'classnames';

import { useEventQuery } from '@vlabs/monokit/entities/events/api';
import {
  AgeAttributeCard,
  GenderAttributeCard,
  EmotionAttributeCard,
  MaskAttributeCard,
} from '@vlabs/monokit/shared/ui/atribute-cards';

import { useEventAttributesTranslation } from './i18n';
import st from './shared.module.sass';
import { buildTestid } from '../../../shared/lib/utils';

interface EventFaceAttributesProps extends WithDataTestid, WithClassNameProp {
  eventId: EventId;
}

const displayName = 'EventFaceAttributes';
const testid = buildTestid(displayName);

function EventFaceAttributes({
  eventId,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: EventFaceAttributesProps): React.JSX.Element {
  const { t } = useEventAttributesTranslation();
  const { data: event } = useEventQuery(eventId);

  if (!event) return <span className={st.NoData}>{t('no_data')}</span>;

  const {
    age, gender, emotion, mask,
  } = event as Record<string, unknown>;

  return (
    <div
      className={cn(st.Attributes, className)}
      {...testid(dataTestId)}
    >
      {!!age && (
        <AgeAttributeCard
          age={age as never}
          className={st.AttributeCard}
        />
      )}

      {!!gender && (
        <GenderAttributeCard
          className={st.AttributeCard}
          gender={gender as never}
        />
      )}

      {!!emotion && (
        <EmotionAttributeCard
          className={st.AttributeCard}
          emotion={emotion as never}
        />
      )}

      {!!mask && (
        <MaskAttributeCard
          className={st.AttributeCard}
          mask={mask as never}
        />
      )}
    </div>
  );
}

EventFaceAttributes.displayName = displayName;

export { EventFaceAttributes };
